import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { shouldRenderRefreshButton } from '../utils/common.utils';

import { PipeActions, setPipesLoadingHasFinished } from '../redux-store/thunks/pipes.thunks';
import { pipesSelector, leafPipesSelector } from 'Redux/selectors';
import { DatasetActions } from 'Redux/thunks/datasets.thunks';
import { SystemActions } from 'Redux/thunks/systems.thunks';
import {
  defaultSortBy,
  defaultVisibleCols,
  defaultVisibleFacets,
  emptyFilterPipes,
  filterLabelMapping,
  getPipesDefaultFilter,
} from '../constants/pipesListTableDefaults';

import { PipesListPage } from '../containers/PipesListPage.container';
import FilterTable from '../components/filter-table/FilterTable';
import { ListActions } from '../components/common/ListActions.view';
import { LoadingPanel } from '../components/common/LoadingPanel';

import useLocalStorage from '../hooks/useLocalStorage';
import { PipesListContext, usePipesListColumns } from '../hooks/usePipesListColumns.hook';
import { StorageKeys } from '../constants/storageKeys';
import { getPipesCurlCommand } from '../utils/pipes.utils';
import { getLineageFacetValues } from '../utils/pipesLineageFilters.utils';

import type { RootState } from 'src/types/state.types';

export const PipesList: React.FC = ({}) => {
  const dispatch = useDispatch();

  const pipes = useSelector((state: RootState) => pipesSelector(state));
  const leafPipes = useSelector((state: RootState) => leafPipesSelector(state));
  const pipesDict = useSelector((state: RootState) => state.pipes);
  const upstreams = useSelector((state: RootState) => state.upstreams);
  const downstreams = useSelector((state: RootState) => state.downstreams);
  const lookups = useSelector((state: RootState) => state.lookups);
  const datasets = useSelector((state: RootState) => state.datasets);
  const pipesLoaded = useSelector((state: RootState) => state.loadStatus.pipesLoaded);
  const systems = useSelector((state: RootState) => state.systems);
  const subUrl = useSelector((state: RootState) => state.subscription.url);
  const token = useSelector((state: RootState) => state.subscription.token);

  const loadPipesForced = () => dispatch(PipeActions.loadAllForced());

  const loadSystems = () => {
    dispatch(SystemActions.loadAll());
  };

  const loadDatasets = () => {
    dispatch(DatasetActions.loadAll());
  };

  const isLiveUpdatesEnabled = useSelector((state: RootState) => state.globals.enableLiveUpdates);

  const isLiveUpdatesSupported = useSelector(
    (state: RootState) => state.subscription.supportsLiveUpdates ?? false
  );
  const failedToConnectToLiveUpdates = useSelector(
    (state: RootState) => state.subscription.failedToConnectToLiveUpdates
  );

  const [showBytesExact, setShowBytesExact] = useLocalStorage(
    StorageKeys.dataTable,
    ['pipes', 'showBytesExact'],
    true
  );

  const toggleShowBytesExact = () => {
    setShowBytesExact(!showBytesExact);
  };

  const [shouldLoadDatasets, setShouldLoadDataset] = useState(false);

  const renderActions = (selectedIds: string[], clearSelection: () => void) => {
    const selectedPipes = getSelectedPipes(selectedIds);

    const deleteBatchPipes = () => {
      dispatch(PipeActions.delete(selectedIds));
      clearSelection();
    };

    return <ListActions selection={selectedPipes} onDelete={deleteBatchPipes} name="pipe" />;
  };

  const getSelectedPipes = (selectedIds: string[]) => {
    return selectedIds?.map((id) => pipesDict?.[id])?.filter(Boolean);
  };

  const handleVisibleColsChanged = useCallback(
    (newCols: string[]) => {
      // in case that systems or datasets are not loaded already, unlikely
      if ((newCols.includes('From') || newCols.includes('To')) && !systems) {
        loadSystems();
      }
      if (
        newCols.includes('Populated') ||
        newCols.includes('Index w/o deleted') ||
        newCols.includes('Complete index') ||
        newCols.includes('Complete log')
      ) {
        if (!datasets) {
          loadDatasets();
        }

        setShouldLoadDataset(true);
      } else {
        setShouldLoadDataset(false);
      }
    },
    [loadSystems, loadDatasets, datasets, systems]
  );

  const handleFilterChanged = useCallback(() => {
    loadPipesForced();
    loadSystems();
  }, [loadPipesForced, loadSystems]);

  const columns = usePipesListColumns(leafPipes, datasets, systems);

  const pipesWithLineageFacets = useMemo(() => {
    return getLineageFacetValues({
      pipes,
      pipesById: pipesDict,
      upstreams,
      downstreams,
      lookups,
    });
  }, [pipes, pipesDict, upstreams, downstreams, lookups]);

  useEffect(() => {
    if (pipes?.length) {
      dispatch(setPipesLoadingHasFinished());
    }
  }, [pipes]);

  if (pipesLoaded === false) {
    return <LoadingPanel loadingMessage="Loading pipes..." />;
  }

  return (
    <PipesListContext.Provider
      value={{
        datasets,
        systems,
        showBytesExact,
        toggleShowBytesExact,
      }}
    >
      <PipesListPage
        includeInternal={false}
        shouldLoadDatasets={shouldLoadDatasets}
        shouldRenderRefreshButton={shouldRenderRefreshButton({
          isLiveUpdatesSupported,
          isLiveUpdatesEnabled,
          failedToConnectToLiveUpdates,
        })}
      >
        <FilterTable
          actionsRenderer={renderActions}
          defaults={{
            filter: getPipesDefaultFilter('user'),
            sortBy: defaultSortBy,
            visibleCols: defaultVisibleCols,
            visibleFacets: defaultVisibleFacets,
          }}
          emptyFilter={emptyFilterPipes}
          filterLabelMapping={filterLabelMapping}
          columns={columns}
          itemsToFilter={pipesWithLineageFacets}
          tableKey="pipes"
          onVisibleColsChanged={handleVisibleColsChanged}
          onFilterChanged={handleFilterChanged}
          curl={getPipesCurlCommand(subUrl, token, false)}
        />
      </PipesListPage>
    </PipesListContext.Provider>
  );
};
